// Session boot + logout. setAuthHint mirrors the real (HttpOnly, cross-domain)
// session cookie into a localStorage flag so the inline boot script in
// index.html can hide the SSR marketing landing for already-authenticated
// users without waiting for /auth/me to resolve. Keeping these calls
// alongside `state.user` assignments is load-bearing for the no-flash UX.
import { state } from "./state.js";
import { api } from "./api.js";
import { clearCache } from "./cache.js";
import { isLoadingButton, withButtonLoading } from "./dom.js";
import { navigate } from "./navigation.js";

// Best-effort: attach user context to any Sentry events captured after this
// point. The Loader Script may still be downloading the full SDK; the loader
// queues setUser calls so they apply once Sentry is ready. No-op if the
// browser Sentry SDK isn't on the page (e.g. local dev without DSN).
function syncSentryUser(user) {
  try {
    const s = window.Sentry;
    if (!s) return;
    if (user) s.setUser({ id: user.id, email: user.email });
    else if (typeof s.setUser === "function") s.setUser(null);
  } catch {
    // Sentry is best-effort; never let observability break the SPA.
  }
}

// Wave 3 (multi-user & roles spec §7): tag company_id/role alongside the
// user, same best-effort contract as syncSentryUser above — errors and
// traces can then be filtered or grouped by company in the Sentry UI.
function syncSentryMembership(membership) {
  try {
    const s = window.Sentry;
    if (!s || typeof s.setTags !== "function") return;
    if (membership) s.setTags({ company_id: membership.companyId, role: membership.role });
    else s.setTags({ company_id: undefined, role: undefined });
  } catch {
    // Sentry is best-effort; never let observability break the SPA.
  }
}

export function setAuthHint(present) {
  try {
    if (present) localStorage.setItem("sendinvo.maybeAuth", "1");
    else localStorage.removeItem("sendinvo.maybeAuth");
  } catch {
    // localStorage can throw in private-mode Safari etc.; the hint is
    // best-effort and the SPA still functions without it.
  }
}

export async function loadSession() {
  try {
    const data = await api("/auth/me");
    state.user = data.user;
    state.membership = data.membership || null;
    state.companies = data.companies || [];
    // Marks the session as fully resolved — membership included. Screens gate
    // their CTAs on can(), which reads state.membership.role, so "we haven't
    // asked yet" and "asked, and they genuinely have no company" have to be
    // distinguishable; handleRouteChange heals the first, not the second.
    state._loaded.session = true;
    if (data?.onboarding) {
      state.onboarding = { ...state.onboarding, ...data.onboarding };
    }
    setAuthHint(Boolean(state.user));
    syncSentryUser(state.user);
    syncSentryMembership(state.membership);
    // Subscription state rides on /auth/me rather than /billing/status: it's a
    // fact about the COMPANY that every role needs (Send vs a subscribe CTA,
    // and whether payment plans are offered), while /billing/status is gated
    // to owner/admin and 403s for a member — who then looked unsubscribed.
    applySubscription(data?.subscription);
  } catch {
    state.user = null;
    state.membership = null;
    state.companies = [];
    state._loaded.session = false;
    setAuthHint(false);
    syncSentryUser(null);
    syncSentryMembership(null);
  }
}

// Write a /auth/me subscription payload onto state. Returns true if it
// changed, so callers can decide whether to re-render.
function applySubscription(payload) {
  const next = {
    subscribed: Boolean(payload?.subscribed),
    status: payload?.status || "none"
  };
  const changed =
    next.subscribed !== state.subscription.subscribed ||
    next.status !== state.subscription.status;
  state.subscription = next;
  return changed;
}

// Re-fetch the cached subscription state (best-effort). Returns true if it
// changed — callers use that to decide whether to re-render. Used when the tab
// regains focus (the user may have subscribed in the new tab opened by
// "Continue to subscribe"); boot gets it from loadSession's own /auth/me.
export async function refreshSubscription() {
  if (!state.user) return false;
  const data = await api("/auth/me").catch(() => null);
  if (!data) return false;
  return applySubscription(data.subscription);
}

// Fetch the Stripe connection state once per session, for surfaces that need
// to know whether an invoice will carry a "Pay online" link — chiefly the
// compose-screen preview, which would otherwise show every user a document
// with bank details only, even though a Stripe-connected user's client
// actually receives a pay-online CTA too.
//
// Cached on state and best-effort: if the call fails we leave the flag false,
// so the preview under-promises rather than showing a CTA the client won't get.
export async function ensureStripeConnectStatus() {
  if (state._loaded.stripeConnect) return state.stripeConnect;
  const status = await api("/stripe/connect/status").catch(() => null);
  if (status) {
    state.stripeConnect = status;
    state._loaded.stripeConnect = true;
  }
  return state.stripeConnect;
}

// True when a sent invoice would carry a "Pay online" link for the client.
// Mirrors buildInvoicePdfModel's canPayByCard (server/document-model.js), minus
// the payment-token check — a draft has no token yet, but the preview is
// showing what the client will receive once it IS sent.
export function canPayOnline() {
  const sc = state.stripeConnect || {};
  return Boolean(sc.connected && sc.chargesEnabled);
}

export async function handleLogout(evt) {
  const trigger = evt?.currentTarget;
  await withButtonLoading(isLoadingButton(trigger) ? trigger : null, async () => {
    await api("/auth/logout", { method: "POST", body: JSON.stringify({}) }).catch(() => null);
    state.user = null;
    state.membership = null;
    state.companies = [];
    state._loaded.session = false;
    setAuthHint(false);
    clearCache();
    syncSentryUser(null);
    syncSentryMembership(null);
    navigate("/");
  });
}
